/** @jsxImportSource @emotion/react */
// React/core
import React, { useCallback, useMemo, useState } from 'react'
// Third-party
import { css } from '@emotion/react'
import { CloseOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
// Relative
import {
  heading4,
  paragraphSmall,
  paragraphXSmall,
  paragraphMedium,
  weightSemiBold,
  weightMedium,
  weightRegular,
  weightBold,
} from 'src/components/css'
import {
  CheckboxCheckedIcon,
  CheckboxOutlineIcon,
  CheckboxIndeterminateIcon,
} from './icons'

export interface PublishDivision {
  id: string
  name: string
  gamesCount?: number
}

interface PublishScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onPublish: (divisionIds: string[]) => void
  divisions: PublishDivision[]
  totalGames: number
  publishedIds?: Set<string>
}

const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const titleStyles = css`
  ${heading4}
  ${weightSemiBold}
  color: ${colors.WHITE};
  margin: 0;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const contentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const summaryStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const summaryTitleStyles = css`
  ${paragraphMedium}
  ${weightBold}
  color: ${colors.WHITE};
  margin: 0;
`

const summarySubtitleStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const tableContainerStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const tableHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px;
  background-color: ${colors.SECONDARY_100};
`

const tableHeaderCellStyles = css`
  ${paragraphXSmall}
  ${weightMedium}
  color: ${colors.NEUTRAL_70};
  flex: 1;
  text-transform: uppercase;
`

const divisionRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_500};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${colors.SECONDARY_300};
  }
`

const altRowStyles = css`
  background-color: ${colors.SECONDARY_400};

  &:hover {
    background-color: ${colors.SECONDARY_300};
  }
`

const divisionNameStyles = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.WHITE};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`

const gamesCellStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  width: 80px;
  flex-shrink: 0;
  text-align: right;
`

const helperTextStyles = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
`

const buttonContainerStyles = css`
  display: flex;
  gap: 16px;
  width: 360px;
`

const cancelButtonStyles = css`
  flex: 1;
  padding: 9px 25px;
  border-radius: 8px;
  border: 1px solid ${colors.WHITE};
  background: transparent;
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const publishButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_DARK};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const checkboxContainerStyles = css`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
`

const publishedRowStyles = css`
  opacity: 0.5;
  cursor: default;

  &:hover {
    background-color: inherit;
  }
`

const publishedBadgeStyles = css`
  ${paragraphXSmall}
  ${weightMedium}
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 4px;
  background: ${colors.SUCCESS_400};
  color: ${colors.WHITE};
  font-size: 12px;
  line-height: 16px;
  vertical-align: middle;
`

const checkboxCheckedContainerStyles = css`
  transform: scale(1.15);
  animation: checkScale 0.25s ease-out;

  @keyframes checkScale {
    0% {
      transform: scale(0.8);
    }
    50% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1.15);
    }
  }
`

const PublishScheduleModal: React.FC<PublishScheduleModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  divisions,
  publishedIds,
}) => {
  const unpublishedDivisions = useMemo(
    () => divisions.filter((d) => !publishedIds?.has(d.id)),
    [divisions, publishedIds]
  )

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(unpublishedDivisions.map((d) => d.id))
  )

  // Reset selection when modal opens — only pre-select unpublished divisions
  React.useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(unpublishedDivisions.map((d) => d.id)))
    }
  }, [isOpen, unpublishedDivisions])

  const selectableDivisions = unpublishedDivisions
  const allSelected =
    selectedIds.size === selectableDivisions.length &&
    selectableDivisions.length > 0
  const noneSelected = selectedIds.size === 0
  const isIndeterminate = !allSelected && !noneSelected

  const selectedGamesCount = useMemo(() => {
    return divisions
      .filter((d) => selectedIds.has(d.id))
      .reduce((sum, d) => sum + (d.gamesCount ?? 0), 0)
  }, [divisions, selectedIds])

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(selectableDivisions.map((d) => d.id)))
    }
  }, [allSelected, selectableDivisions])

  const toggleDivision = useCallback((divisionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(divisionId)) {
        next.delete(divisionId)
      } else {
        next.add(divisionId)
      }
      return next
    })
  }, [])

  const handlePublish = useCallback(() => {
    onPublish(Array.from(selectedIds))
    onClose()
  }, [onPublish, onClose, selectedIds])

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div css={headerStyles}>
        <h2 css={titleStyles}>Publish Public Schedule</h2>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={onClose}
          aria-label='Close modal'
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Content */}
      <div css={contentStyles}>
        <div css={summaryStyles}>
          <p css={summaryTitleStyles}>Summary</p>
          <p css={summarySubtitleStyles}>
            {selectedIds.size} of {selectableDivisions.length} division
            {selectableDivisions.length !== 1 ? 's' : ''} selected
            {publishedIds && publishedIds.size > 0
              ? ` · ${publishedIds.size} already published`
              : ''}
            {selectedGamesCount > 0
              ? ` · ${selectedGamesCount} game${
                  selectedGamesCount !== 1 ? 's' : ''
                }`
              : ''}
          </p>
        </div>

        {/* Divisions table */}
        <div css={tableContainerStyles}>
          <div css={tableHeaderStyles}>
            <div
              css={[
                checkboxContainerStyles,
                allSelected && checkboxCheckedContainerStyles,
                css`
                  cursor: pointer;
                `,
              ]}
              onClick={toggleSelectAll}
            >
              {allSelected ? (
                <CheckboxCheckedIcon />
              ) : isIndeterminate ? (
                <CheckboxIndeterminateIcon />
              ) : (
                <CheckboxOutlineIcon />
              )}
            </div>
            <span css={tableHeaderCellStyles}>Division</span>
            <span
              css={css`
                ${tableHeaderCellStyles};
                width: 80px;
                flex: none;
                text-align: right;
              `}
            >
              Games
            </span>
          </div>

          {divisions.map((division, index) => {
            const isPublished = publishedIds?.has(division.id) ?? false
            const isSelected = selectedIds.has(division.id)
            return (
              <div
                key={division.id}
                css={[
                  divisionRowStyles,
                  index % 2 === 1 && altRowStyles,
                  isPublished && publishedRowStyles,
                ]}
                onClick={
                  isPublished ? undefined : () => toggleDivision(division.id)
                }
              >
                <div
                  css={[
                    checkboxContainerStyles,
                    isSelected && checkboxCheckedContainerStyles,
                  ]}
                >
                  {isPublished ? (
                    <CheckboxCheckedIcon />
                  ) : isSelected ? (
                    <CheckboxCheckedIcon />
                  ) : (
                    <CheckboxOutlineIcon />
                  )}
                </div>
                <span css={divisionNameStyles}>
                  {division.name}
                  {isPublished && (
                    <span css={publishedBadgeStyles}>Published</span>
                  )}
                </span>
                <span css={gamesCellStyles}>
                  {division.gamesCount != null ? division.gamesCount : '—'}
                </span>
              </div>
            )
          })}
        </div>

        <p css={helperTextStyles}>
          Publishing will commit games from the selected divisions to your
          schedule. Any previously published games in those divisions will be
          replaced.
        </p>
      </div>

      {/* Footer */}
      <div css={footerStyles}>
        <div css={buttonContainerStyles}>
          <button type='button' css={cancelButtonStyles} onClick={onClose}>
            CANCEL
          </button>
          <button
            type='button'
            css={publishButtonStyles}
            onClick={handlePublish}
            disabled={noneSelected}
          >
            PUBLISH
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default PublishScheduleModal
